/**
 * What the planner and the visitor's page need to know about "só facilitador" (THE-1698), as pure
 * functions — `klapzo-web` has no jsdom, so the logic worth testing lives here and the screens only
 * call it.
 */
import type { DocxModel } from "./agendaDocx.js";
import { agendaToCsv, recomputeAgenda, sharedSchedule, type AgendaBlockInput, type CsvHeaders, type SharedAgendaWire } from "@klapzo/kit-core";

/** How many blocks a share link will show and hide — the numbers the confirm dialog states. */
export function shareSplit(blocks: readonly AgendaBlockInput[]): { total: number; hidden: number; visible: number } {
  const hidden = blocks.filter((b) => b.facilitatorOnly === true).length;
  return { total: blocks.length, hidden, visible: blocks.length - hidden };
}

/**
 * The visitor's CSV. Built from the share link's own response — the same object the page renders —
 * so it has no way to carry a block the link did not: there is no second copy of the agenda here
 * to forget a filter on. (The owner's CSV is the planner's, from the complete draft.)
 */
export function sharedCsv(shared: SharedAgendaWire, headers: CsvHeaders): string {
  return agendaToCsv(sharedSchedule(shared), headers);
}

/** Flip one block's marker, leaving the others (and their order) alone. */
export function toggleFacilitatorOnly(blocks: readonly AgendaBlockInput[], id: string): AgendaBlockInput[] {
  return blocks.map((b) => (b.id === id ? { ...b, facilitatorOnly: b.facilitatorOnly !== true } : b));
}

/**
 * The owner's .docx model (THE-6012): the complete plan, `facilitatorOnly` blocks included and
 * labelled — the planner's own CSV/JSON are complete too. `methodTitles` maps methodId → card title.
 */
export function ownerDocxModel(
  title: string,
  blocks: readonly AgendaBlockInput[],
  startClock: string | undefined,
  methodTitles: ReadonlyMap<string, string>,
): DocxModel {
  const s = recomputeAgenda(blocks, startClock);
  return {
    title,
    startClock: startClock && s.endClock ? startClock : null,
    endClock: s.endClock ?? null,
    totalMin: s.totalMin,
    rows: s.blocks.map((b) => ({
      order: b.order + 1,
      start: b.startClock ?? null,
      title: b.title,
      durationMin: b.durationMin,
      method: b.methodId ? (methodTitles.get(b.methodId) ?? null) : null,
      facilitatorOnly: b.facilitatorOnly === true,
    })),
  };
}

/**
 * The visitor's .docx model. Built from the share link's own response, like `sharedCsv`: there is no
 * second copy of the agenda to forget the filter on. The wire has no method, so the file has none.
 */
export function sharedDocxModel(shared: SharedAgendaWire): DocxModel {
  const s = sharedSchedule(shared);
  return {
    title: shared.title,
    startClock: shared.startClock && s.endClock ? shared.startClock : null,
    endClock: s.endClock ?? null,
    totalMin: s.totalMin,
    rows: s.blocks.map((b) => ({
      order: b.order + 1,
      start: b.startClock ?? null,
      title: b.title,
      durationMin: b.durationMin,
      method: null,
      facilitatorOnly: false,
    })),
  };
}
